import { faSpinner } from '@awesome.me/kit-5de77b2c02/icons/duotone/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cx from 'classnames'
import {
  ChangeEvent,
  FC,
  Fragment,
  useEffect,
  useReducer,
  useState
} from 'react'
import { createInitAction } from '../../helpers/useReducerUtil'
import { CurrencyValues } from '../../store/filter/types'
import { QuoteReliabilityDisplayNames } from '../../store/securities/types'
import { useAppDispatch, useAppSelector } from '../../store/types'
import {
  fetchUserEditorData,
  saveUserEditorData
} from '../../store/userEditorData/actions'
import { getUserEditorDataById } from '../../store/userEditorData/selectors'
import { UserEditorData } from '../../store/userEditorData/types'
import Checkbox from '../Checkbox'
import ComponentFooter from '../ComponentFooter/ComponentFooter'
import ComponentHeader from '../ComponentHeader/ComponentHeader'
import HeaderCloseButton from '../ComponentHeader/HeaderCloseButton'
import GenericDropdownMenu from '../GenericDropdownMenu/GenericDropdownMenu'
import GenericDropdownMenuMulti from '../GenericDropdownMenu/GenericDropdownMenuMulti'
import Modal from '../GenericModal/Modal'
import SubAccountEditor from './SubAccountEditor'
import {
  brokerRows,
  currencySummary,
  initState,
  reducer,
  salesPersonKeys,
  toggleSubAccount,
  toSavePayload,
  toWorkingUser,
  updateActionCreator,
  WorkingUser
} from './userEditorForm'

import styles from './UserEditor.module.scss'

interface Props {
  closeFunc: () => void
  userId: number
  wrapModal: boolean
}

type FlagKey = KeysWithValsOfType<WorkingUser, boolean>
type TextKey = KeysWithValsOfType<WorkingUser, string>

const qrOptions = Object.entries(QuoteReliabilityDisplayNames).map(
  ([value, label]) => ({ value, label })
)

interface FormProps {
  user: UserEditorData
  closeFunc: () => void
}

const UserEditorForm: FC<FormProps> = ({ user, closeFunc }) => {
  const dispatch = useAppDispatch()
  const [working, localDispatch] = useReducer(reducer, user, (u) =>
    initState(toWorkingUser(u))
  )
  const [error, setError] = useState('')

  const { isChanged } = working
  useEffect(() => {
    // a refetch landing mid-edit must not overwrite what the admin has typed
    if (isChanged) return
    localDispatch(createInitAction(toWorkingUser(user)))
  }, [user, isChanged])

  const update = <K extends keyof WorkingUser>(key: K, value: WorkingUser[K]) =>
    localDispatch(updateActionCreator(key, value))

  const flag = (key: FlagKey) => ({
    locator: '',
    checked: working[key],
    onChange: () => update(key, !working[key])
  })

  const text = (key: TextKey) => ({
    type: 'text',
    value: working[key],
    onChange: (e: ChangeEvent<HTMLInputElement>) =>
      update(key, e.currentTarget.value)
  })

  const brokerNames = user.possibleBrokers.map((u) => u.userName)

  const handleApply = () => {
    const result = toSavePayload(working, user)
    if ('error' in result) {
      setError(result.error)
      return
    }
    dispatch(saveUserEditorData(result.payload))
    closeFunc()
  }

  return (
    <>
      <ComponentHeader
        title={`User editor for ${user.userName}`}
        headerButtons={<HeaderCloseButton onClick={closeFunc} />}
      />
      <form className={styles.userEditorForm}>
        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>General Account Information</legend>

          <div className={styles.accountContainer}>
            <div className={styles.accountRow}>
              <label className={styles.accountLabel}>User Name:</label>
              <input {...text('userName')} />
              <label className={styles.accountLabel}>
                User Classification:
              </label>
              <div className={styles.accountDropdown}>
                <GenericDropdownMenu
                  setSelectedItem={(v) => update('userClassification', v)}
                  options={user.possibleUserClassifications.map(
                    (uc) => uc.userClassName
                  )}
                  selectedItem={working.userClassification}
                  selectId="UserClassifications"
                  openRight={false}
                  className={cx(
                    styles.userClassDropdown,
                    styles.squareDropdown
                  )}
                />
              </div>
            </div>

            <div className={styles.accountRow}>
              <label className={styles.accountLabel}>Logon Name:</label>
              <input {...text('logonName')} />
              <label className={styles.accountLabel}>Account:</label>
              <div className={styles.accountDropdown}>
                <GenericDropdownMenu
                  setSelectedItem={(v) => update('account', v)}
                  options={user.possibleCustomerRecords.map(
                    (c) => c.customerName
                  )}
                  selectedItem={working.account}
                  selectId="UserAccount"
                  openRight={false}
                  className={cx(
                    styles.userClassDropdown,
                    styles.squareDropdown
                  )}
                />
              </div>
            </div>

            <div className={styles.accountRow}>
              <label className={styles.accountLabel}>Logon Password:</label>
              <input
                {...text('loginPassword')}
                type="password"
                placeholder="******"
              />
              <label className={styles.accountLabel}>Email Address:</label>
              <div className={styles.accountDropdown}>
                <input {...text('emailAddress')} />
              </div>
            </div>
          </div>

          <div className={cx(styles.checkboxContainer, styles.spacedRow)}>
            <Checkbox {...flag('isWebUser')}>Web User</Checkbox>
            <Checkbox {...flag('ableToSeeTradersTheyCover')}>
              Able to see traders they cover
            </Checkbox>
            <Checkbox {...flag('ableToSeeAllTradersAtFirm')}>
              Able to see all traders at firm
            </Checkbox>
          </div>

          <div className={styles.checkboxAccountContainer}>
            <label>User Privileges</label>
            <div className={styles.checkboxContainer}>
              <Checkbox {...flag('isAdmin')}>Admin</Checkbox>
              <Checkbox {...flag('isBackOffice')}>Back Office</Checkbox>
              <Checkbox {...flag('isBroker')}>Broker</Checkbox>
            </div>
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Sub Accounts</legend>
          <div className={styles.subAccountContainer}>
            <SubAccountEditor
              subAccounts={user.subAccounts}
              enabledIds={working.enabledSubAccountIds}
              defaultId={working.defaultSubAccountId}
              onToggle={(custId) =>
                update(
                  'enabledSubAccountIds',
                  toggleSubAccount(working.enabledSubAccountIds, custId)
                )
              }
              onSetDefault={(custId) => update('defaultSubAccountId', custId)}
            />
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Broker Coverage</legend>
          <div className={styles.brokerGrid}>
            {brokerRows.map(({ label, slots }) => (
              <Fragment key={label}>
                <span className={styles.brokerRowLabel}>{label}</span>
                {slots.map(([nameKey, pctKey]) => (
                  <div key={nameKey} className={styles.brokerCell}>
                    <div className={styles.fullWidthDropdown}>
                      <GenericDropdownMenu
                        setSelectedItem={(v) => update(nameKey, v)}
                        options={brokerNames}
                        selectedItem={working[nameKey]}
                        selectId={nameKey}
                        openRight={true}
                        className={cx(
                          styles.fillDropdown,
                          styles.squareDropdown
                        )}
                        placeholder="Select Broker"
                      />
                    </div>
                    <input
                      {...text(pctKey)}
                      type="number"
                      className={styles.brokerPercent}
                    />
                    %
                  </div>
                ))}
              </Fragment>
            ))}
          </div>
        </fieldset>

        <div className={styles.smallFieldsetGroup}>
          <fieldset className={cx(styles.fieldset, styles.smallFieldset)}>
            <legend className={styles.legend}>Salesperson Coverage</legend>
            <label>Salesperson(s) Who Cover:</label>
            <div className={styles.salesPersonList}>
              {salesPersonKeys.map((key) => (
                <div key={key} className={styles.fullWidthDropdown}>
                  <GenericDropdownMenu
                    setSelectedItem={(v) => update(key, v)}
                    options={brokerNames}
                    selectedItem={working[key]}
                    selectId={key}
                    openRight={true}
                    className={cx(styles.fillDropdown, styles.squareDropdown)}
                    placeholder="Select Salesperson"
                  />
                </div>
              ))}
            </div>
          </fieldset>

          <fieldset className={cx(styles.fieldset, styles.smallFieldset)}>
            <legend className={styles.legend}>Enabled Currencies</legend>
            <GenericDropdownMenuMulti
              placeholder="Currency"
              dropdownLabel={currencySummary(
                working.currencies,
                CurrencyValues
              )}
              selectedItems={working.currencies}
              options={CurrencyValues}
              setSelectedItems={(v) => update('currencies', v)}
              selectId="UserCurrencies"
              openRight={true}
              className={styles.squareDropdown}
            />
          </fieldset>

          <fieldset className={cx(styles.fieldset, styles.smallFieldset)}>
            <legend className={styles.legend}>Last Connected From Mac</legend>
            <label>Valid MACs:</label>
            <textarea
              rows={4}
              value={working.validMACs}
              onChange={(e) => update('validMACs', e.currentTarget.value)}
            />
          </fieldset>
        </div>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Miscellaneous</legend>
          <div className={styles.qrContainer}>
            <label>QR (Fill Rate) Color:</label>
            <select
              data-testid="qr-select"
              value={working.qr}
              onChange={(e) => update('qr', e.currentTarget.value)}
            >
              {qrOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className={cx(styles.qrBox, styles[working.qr])} />
          </div>

          <div className={cx(styles.checkboxContainer, styles.spacedRow)}>
            <Checkbox {...flag('canRunReport')}>Can Run Reports</Checkbox>
            <Checkbox {...flag('highlightWhenNotLogged')}>
              Highlight when not logged
            </Checkbox>
            <Checkbox {...flag('markAsDeleted')}>Mark as Deleted</Checkbox>
          </div>
          <div className={styles.inputContainer}>
            <label>Last click through version:</label>
            <input {...text('lastClickThroughVersion')} />
            <label>Default FIX Account Alias:</label>
            <input {...text('defaultFIXAccountAlias')} />
            <label>Max Single Order Limit:</label>
            <input {...text('maxSingleOrderLimit')} />
          </div>
        </fieldset>

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Bloomberg Info</legend>
          <div className={styles.inputContainer}>
            <label>Bloomberg UUID:</label>
            <input {...text('bloombergUUID')} />
          </div>
        </fieldset>
      </form>
      <ComponentFooter
        onCancelClick={closeFunc}
        onApplyClick={handleApply}
        errorText={error}
      />
    </>
  )
}

const UserEditorModal: FC<Props> = ({ closeFunc, userId, wrapModal }) => {
  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(fetchUserEditorData(userId))
  }, [dispatch, userId])

  const user = useAppSelector(getUserEditorDataById)(userId)
  const content = user ? (
    <UserEditorForm key={userId} user={user} closeFunc={closeFunc} />
  ) : (
    <div className="loading-error-container">
      <FontAwesomeIcon icon={faSpinner} spin /> Loading...
    </div>
  )

  return wrapModal ? content : <Modal>{content}</Modal>
}

export default UserEditorModal
